import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { useProxyData } from '../contexts/ProxyDataContext';
import { useProxyWorkflowContext } from '../hooks/useProxyWorkflowContext';
import { buildProxyIntakeTasks, buildProxyIntakeSummary, PROXY_WORKFLOW_REGISTRY, type ProxyWorkflowType } from '../data/proxyWorkflowRegistry';
import { type ProxyBand } from '../data/mockProxyJobs';
import { ProxyTaskTable } from '../components/proxy/ProxyTaskTable';
import { useProxyEscalation } from '../hooks/useProxyEscalation';
import { PageHeader } from '../components/PageHeader';
import { resolveProxyJob } from '../utils/proxyQueue';
import { pillStyle } from '../utils/pillStyle';
import { inWindow, type ScopeValue } from '../utils/timeWindow';
import { proxyWindowBounds, proxyWindowCounts } from '../utils/proxyTimeWindow';

const PROXY_BANDS: ProxyBand[] = ['Red', 'Yellow', 'Green'];
const BAND_PILL_COLOR: Record<ProxyBand, { bg: string; fg: string; border: string }> = {
  Red:    { bg: '#FEF2F2', fg: '#b91c1c', border: '#FECACA' },
  Yellow: { bg: '#FFFBEB', fg: '#92400E', border: '#FCD34D' },
  Green:  { bg: '#F0FDF4', fg: '#166534', border: '#BBF7D0' },
};

export function ProxyIntakeQueuePage() {
  const { setTopbar } = useAppShell();
  const [searchParams] = useSearchParams();
  const { jobs } = useProxyData();
  const ctx = useProxyWorkflowContext();
  const { escalate } = useProxyEscalation();
  // A link to a specific task (?taskId=) opens on the last 30 days so the
  // task is on screen even if it came in before today.
  const [scope, setScope] = useState<ScopeValue>(() => ({ window: searchParams.get('taskId') ? 'month' : 'today' }));
  const [workflowFilter, setWorkflowFilter] = useState<ProxyWorkflowType | 'All'>('All');
  const [bandFilter, setBandFilter] = useState<ProxyBand | null>(null);

  useEffect(() => {
    setTopbar('Proxy Intake Queue', "Everything that's come in for Proxy. Ace proposes a workflow and you validate it once.");
  }, [setTopbar]);

  const rows = useMemo(() => {
    const tasks = buildProxyIntakeTasks(ctx);
    return tasks.map(task => ({ task, job: jobs.find(j => j.jobId === task.jobId)! })).filter(r => r.job);
  }, [ctx, jobs]);

  // Task createdAt is sometimes date-only, sometimes "YYYY-MM-DD HH:MM" —
  // slice to the date before comparing against the window bounds. Proxy's
  // demo data is pinned to PROXY_SEED_TODAY rather than the real calendar
  // date, so that's the "today" the Period control anchors to.
  const bounds = useMemo(() => proxyWindowBounds(scope), [scope]);
  const windowedRows = useMemo(
    () => rows.filter(r => inWindow(r.task.createdAt.slice(0, 10), bounds)),
    [rows, bounds]
  );
  const counts = useMemo(
    () => proxyWindowCounts(rows.map(r => ({ intake_date: r.task.createdAt.slice(0, 10) }))),
    [rows]
  );

  // Only offer workflow types that actually appear in this window — no dead options.
  const workflowOptions = useMemo(() => {
    const present = new Set(windowedRows.map(r => r.task.workflowType));
    return Object.keys(PROXY_WORKFLOW_REGISTRY)
      .filter((t): t is ProxyWorkflowType => present.has(t as ProxyWorkflowType))
      .sort((a, b) => PROXY_WORKFLOW_REGISTRY[a].label.localeCompare(PROXY_WORKFLOW_REGISTRY[b].label));
  }, [windowedRows]);

  const filteredRows = useMemo(() => windowedRows.filter(r => {
    if (workflowFilter !== 'All' && r.task.workflowType !== workflowFilter) return false;
    if (bandFilter && resolveProxyJob(r.job, null).band !== bandFilter) return false;
    return true;
  }), [windowedRows, workflowFilter, bandFilter]);

  const summary = useMemo(() => buildProxyIntakeSummary(ctx), [ctx]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader scope={scope} onScopeChange={setScope} counts={counts} />
      <div className="flex-1 overflow-auto px-6 py-6">
        <div data-tour="intake-summary" className="text-sm text-copy mb-4">
          Ace read <b className="text-ink">{summary.emailsRead}</b> emails → created <b className="text-ink">{summary.tasksCreated}</b> tasks.
        </div>
        <div data-tour="intake-filters" className="flex items-center gap-3 mb-4 flex-wrap gap-y-2">
          {/* Band — few values, so a pill row; mirrors the Category pill row elsewhere. */}
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setBandFilter(null)} className="px-3 py-1 rounded-full text-xs font-medium transition-colors" style={pillStyle(!bandFilter)}>All Bands</button>
            {PROXY_BANDS.map(b => (
              <button key={b} onClick={() => setBandFilter(bandFilter === b ? null : b)}
                className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
                style={pillStyle(bandFilter === b, BAND_PILL_COLOR[b])}>
                {b}
              </button>
            ))}
          </div>
          <div className="w-px h-5 bg-trim" />
          {/* Workflow — many types, used rarely; a compact dropdown rather than a pill row. */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted">Workflow</span>
            <select value={workflowFilter} onChange={e => setWorkflowFilter(e.target.value as ProxyWorkflowType | 'All')}
              className="px-2.5 py-1 rounded-lg border border-trim text-xs text-ink bg-white outline-none focus:border-teal-400 cursor-pointer">
              <option value="All">All workflows</option>
              {workflowOptions.map(t => (
                <option key={t} value={t}>{PROXY_WORKFLOW_REGISTRY[t].label}</option>
              ))}
            </select>
          </div>
        </div>
        <ProxyTaskTable
          rows={filteredRows}
          emptyTitle="Queue is clear"
          emptyBody="Nothing needs ACE's attention right now."
          showEscalate
          onEscalate={escalate}
          highlightTaskId={searchParams.get('taskId')}
          pinWorkflowType="SHORTAGE_FOLLOWUP"
        />
      </div>
    </div>
  );
}
